// ============================================================================
// Buffer Descriptor Table — SharedArrayBuffer Registry
// ============================================================================
// Structured region describing all active buffers. Both Main Thread and
// Worker can read; only Worker writes (via Orchestrator).
//
// Header (8 bytes):  [count: u32] [reserved: u32]
// Entry (32 bytes):  [bufferId: u32] [bufferPtr: u32] [width: u32] [height: u32]
//                    [stride: u32] [format: u8] [flags: u8] [layerOrder: u16]
//                    [reserved: 8 bytes]
import { DESCRIPTOR_TABLE_HEADER_SIZE, BUFFER_ENTRY_SIZE, BufferFlags, } from '../types.js';
export class DescriptorTable {
    view;
    int32;
    base;
    maxEntries;
    /**
     * Generation counter — stored in the SAB header at offset +4 (the formerly
     * "reserved" u32) for cross-thread visibility via Atomics.
     *
     * Incremented on every structural mutation (add, remove, clear). Consumers
     * that cache descriptor indices can Atomics.load this value to detect when
     * their cached indices may be stale due to swap-remove reordering.
     *
     * NOTE: removeByIndex uses swap-with-last — the last entry is moved into the
     * removed slot. Any code caching entry indices must re-lookup after a remove.
     */
    get _generationIndex() {
        return (this.base + 4) >> 2; // header offset +4, the former "reserved" field
    }
    constructor(sab, baseOffset, maxEntries = 64) {
        this.view = new DataView(sab);
        this.int32 = new Int32Array(sab);
        this.base = baseOffset;
        this.maxEntries = maxEntries;
    }
    /**
     * Monotonically increasing counter that changes on any structural mutation.
     * Stored in the SAB header — safe to read from any thread via Atomics.load.
     */
    get generation() {
        return Atomics.load(this.int32, this._generationIndex) >>> 0;
    }
    incrementGeneration() {
        Atomics.add(this.int32, this._generationIndex, 1);
    }
    get count() {
        return Atomics.load(this.int32, this.base >> 2);
    }
    setCount(n) {
        Atomics.store(this.int32, this.base >> 2, n);
    }
    entryOff(i) {
        return this.base + DESCRIPTOR_TABLE_HEADER_SIZE + i * BUFFER_ENTRY_SIZE;
    }
    addEntry(entry) {
        const idx = this.count;
        if (idx >= this.maxEntries) {
            throw new Error(`BufferDescriptorTable is full (${this.maxEntries} entries). ` +
                `Cannot add buffer ${entry.bufferId}. Increase maxDescriptorEntries in allocateControlMemory().`);
        }
        this.writeEntry(idx, entry);
        this.setCount(idx + 1);
        this.incrementGeneration();
        return idx;
    }
    writeEntry(index, e) {
        const o = this.entryOff(index);
        this.view.setUint32(o, e.bufferId, true);
        this.view.setUint32(o + 4, e.bufferPtr, true);
        this.view.setUint32(o + 8, e.width, true);
        this.view.setUint32(o + 12, e.height, true);
        this.view.setUint32(o + 16, e.stride, true);
        this.view.setUint8(o + 20, e.format);
        this.view.setUint8(o + 21, e.flags);
        this.view.setUint16(o + 22, e.layerOrder, true);
        // Zero reserved
        for (let i = 24; i < 32; i++)
            this.view.setUint8(o + i, 0);
    }
    readEntry(index) {
        if (index < 0 || index >= this.count)
            return null;
        const o = this.entryOff(index);
        return {
            bufferId: this.view.getUint32(o, true),
            bufferPtr: this.view.getUint32(o + 4, true),
            width: this.view.getUint32(o + 8, true),
            height: this.view.getUint32(o + 12, true),
            stride: this.view.getUint32(o + 16, true),
            format: this.view.getUint8(o + 20),
            flags: this.view.getUint8(o + 21),
            layerOrder: this.view.getUint16(o + 22, true),
        };
    }
    readAll() {
        const out = [];
        for (let i = 0; i < this.count; i++) {
            const e = this.readEntry(i);
            if (e)
                out.push(e);
        }
        return out;
    }
    findById(id) {
        for (let i = 0; i < this.count; i++) {
            if (this.view.getUint32(this.entryOff(i), true) === id)
                return i;
        }
        return -1;
    }
    /**
     * Remove entry at index using swap-with-last strategy.
     *
     * WARNING: This moves the last entry into the removed slot. Any code that
     * has cached the index of the last entry will now find it at `index` instead.
     * Check `generation` to detect when cached indices are stale.
     */
    removeByIndex(index) {
        const c = this.count;
        if (index < 0 || index >= c)
            return false;
        if (index < c - 1) {
            const last = this.readEntry(c - 1);
            this.writeEntry(index, last);
        }
        this.setCount(c - 1);
        this.incrementGeneration();
        return true;
    }
    removeById(id) {
        const idx = this.findById(id);
        return idx >= 0 ? this.removeByIndex(idx) : false;
    }
    setFlags(index, flags) {
        // Flags byte is at offset 21 within the entry, which is byte position 1
        // within the int32 at entry offset +20. Use CAS for atomic read-modify-write.
        const wordOffset = this.entryOff(index) + 20;
        const wordIndex = wordOffset >> 2;
        const bytePos = 1; // flags is at byte 1 of the [format, flags, layerOrder_lo, layerOrder_hi] word
        const mask = ~(0xFF << (bytePos * 8));
        const bits = (flags & 0xFF) << (bytePos * 8);
        let oldVal;
        let newVal;
        do {
            oldVal = Atomics.load(this.int32, wordIndex);
            newVal = (oldVal & mask) | bits;
        } while (Atomics.compareExchange(this.int32, wordIndex, oldVal, newVal) !== oldVal);
    }
    getFlags(index) {
        // Atomic read of the containing int32, extract flags byte
        const wordOffset = this.entryOff(index) + 20;
        const wordIndex = wordOffset >> 2;
        const word = Atomics.load(this.int32, wordIndex);
        return (word >>> 8) & 0xFF; // byte position 1
    }
    markDirty(index) {
        const wordOffset = this.entryOff(index) + 20;
        const wordIndex = wordOffset >> 2;
        let oldVal;
        let newVal;
        do {
            oldVal = Atomics.load(this.int32, wordIndex);
            newVal = oldVal | (BufferFlags.DIRTY << 8); // DIRTY bit in byte position 1
        } while (Atomics.compareExchange(this.int32, wordIndex, oldVal, newVal) !== oldVal);
    }
    clearDirty(index) {
        const wordOffset = this.entryOff(index) + 20;
        const wordIndex = wordOffset >> 2;
        let oldVal;
        let newVal;
        do {
            oldVal = Atomics.load(this.int32, wordIndex);
            newVal = oldVal & ~(BufferFlags.DIRTY << 8); // clear DIRTY bit in byte position 1
        } while (Atomics.compareExchange(this.int32, wordIndex, oldVal, newVal) !== oldVal);
    }
    clearAllDirty() {
        for (let i = 0; i < this.count; i++)
            this.clearDirty(i);
    }
    getDirtyEntries() {
        return this.readAll().filter(e => (e.flags & BufferFlags.DIRTY) !== 0);
    }
    getCompositeLayers() {
        return this.readAll()
            .filter(e => (e.flags & BufferFlags.COMPOSITE) !== 0)
            .sort((a, b) => a.layerOrder - b.layerOrder);
    }
    clear() { this.setCount(0); this.incrementGeneration(); }
}
//# sourceMappingURL=descriptor-table.js.map